import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { createGlobalStyle } from 'styled-components'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { GenericHeaderBackButton } from 'src/consumer/components/MyPlansPage/MyPlanComponents'
import {
  downloadPrescriptionPdfOfEventAsClinicAssistant,
  getPrescriptionOfEventAsClinicAssistant,
} from 'src/consumer/services/featureServices'
import { PMPButton } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import ViewOnlyPrescriptionV2 from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/ViewOnlyPrescriptionV2'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessful,
} from 'src/mentor/helpers/utilityFunctions'
import { PrescriptionIndividualPageParent } from './ClinicManageBookingsPageStyles'

// ~6mm (0.236in) top/bottom safe-area baked into each printed page, keeping the header/footer
// clear of the printer's hardware non-printable edge even when the user picks "Margins: None".
const SAFE_PRINT_INSET_INCHES = 0.236

// Top-level print stylesheet for the prescription print page. Mirrors the other prescription
// print paths (usePrintActions / PrescriptionTemplateForm) which inject a proper top-level
// `@page` rule — the one previously nested inside a styled-component selector was emitted
// scoped to that class and ignored by the browser. We use `margin: 0` (full-bleed page box) and
// instead bake the safe top/bottom inset into the content flow via SAFE_PRINT_INSET_INCHES, so it
// survives "Margins: None" (which forces @page margin to 0) and prints consistently regardless of
// the dialog's margin choice. print-color-adjust must live on the elements (it is a no-op inside
// `@page`) to keep background graphics/colours in the printout.
const PrescriptionPrintGlobalStyle = createGlobalStyle`
  @media print {
    @page {
      size: A4 portrait;
      margin: 0;
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export default function ClinicPrescriptionPage() {
  const { clinicId, bookingId } = useParams()
  const [prescriptionData, setPrescriptionData] = useState()
  const [prescriptionViewSettings, setPrescriptionViewSettings] = useState()
  const [doctorEmrSections, setDoctorEmrSections] = useState()
  const [dataLoading, setDataLoading] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  async function setupForAssistant() {
    const response = await getPrescriptionOfEventAsClinicAssistant(bookingId)
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      const crData = getDataMapFromCommonResponse(response)
      setPrescriptionData(response?.data?.data?.metaPrescription)
      setPrescriptionViewSettings(crData?.prescriptionViewSettings)
      setDoctorEmrSections(crData?.doctorEmrSections)
    } else {
      setPrescriptionData(null)
    }
  }

  async function setThingsUp() {
    setDataLoading(true)
    await Promise.all([setupForAssistant()])
    setDataLoading(false)
  }

  async function handleDownloadPdf() {
    if (downloadingPdf) {
      return
    }
    setDownloadingPdf(true)
    try {
      const response = await downloadPrescriptionPdfOfEventAsClinicAssistant(bookingId)
      if (response?.status === 200 && response?.data) {
        const contentDisposition = response.headers?.['content-disposition']
        let filename = 'prescription.pdf'
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '')
          }
        }
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)
        toast.success('Prescription PDF downloaded')
      } else {
        toast.error('Could not download prescription PDF')
      }
    } catch (err) {
      console.error('Error downloading prescription PDF', err)
      toast.error('Could not download prescription PDF')
    } finally {
      setDownloadingPdf(false)
    }
  }

  useEffect(() => {
    setThingsUp()
  }, [])

  return (
    <>
      <PrescriptionPrintGlobalStyle />
      {dataLoading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <PrescriptionIndividualPageParent>
          <div
            className="hide-on-print"
            style={{
              margin: '0 auto 20px',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              maxWidth: '800px',
            }}
          >
            <GenericHeaderBackButton
              clickfunction={() => window.history.back()}
              styles={{ cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <PMPButton whitebg onClick={handleDownloadPdf} disabled={downloadingPdf}>
                {downloadingPdf ? 'Downloading…' : 'Download PDF'}
              </PMPButton>
              <PMPButton onClick={() => window.print()}>Print</PMPButton>
            </div>
          </div>
          {/* <ViewOnlyPrescription prescriptionData={prescriptionData} /> */}
          <ViewOnlyPrescriptionV2
            prescriptionData={prescriptionData}
            prescriptionViewSettings={prescriptionViewSettings}
            doctorEmrSections={doctorEmrSections}
            forAssistant
            safePrintInsetInches={SAFE_PRINT_INSET_INCHES}
          />
        </PrescriptionIndividualPageParent>
      )}
    </>
  )
}
